import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUsers, 
  FaProjectDiagram, 
  FaEnvelope, 
  FaBell,
  FaCode,
  FaStar,
  FaEye,
  FaDownload,
  FaNewspaper,
  FaChartLine
} from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiService.getDashboardStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        // Fallback stats
        setStats({
          visitors: 1247,
          projects: 15,
          messages: 23,
          notifications: 5,
          githubStars: 0,
          pageViews: 3456,
          downloads: 89,
          blogPosts: 12
        });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const statCards = [
    { label: 'Visitors', value: stats.visitors, icon: FaUsers, color: 'from-blue-500 to-cyan-500' },
    { label: 'Projects', value: stats.projects, icon: FaProjectDiagram, color: 'from-green-500 to-emerald-500' },
    { label: 'Messages', value: stats.messages, icon: FaEnvelope, color: 'from-purple-500 to-pink-500' },
    { label: 'Notifications', value: stats.notifications, icon: FaBell, color: 'from-yellow-500 to-orange-500' },
    { label: 'GitHub Stars', value: stats.githubStars, icon: FaStar, color: 'from-red-500 to-rose-500' },
    { label: 'Page Views', value: stats.pageViews, icon: FaEye, color: 'from-indigo-500 to-violet-500' },
    { label: 'Downloads', value: stats.downloads, icon: FaDownload, color: 'from-teal-500 to-cyan-500' },
    { label: 'Blog Posts', value: stats.blogPosts, icon: FaNewspaper, color: 'from-orange-500 to-red-500' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">Welcome back! Here's what's happening with your portfolio.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Last updated: Today</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{stat.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stat.color} bg-opacity-20 flex items-center justify-center`}>
                <stat.icon className="text-white text-xl" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-2xl p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaChartLine className="text-primary-400" />
            Recent Activity
          </h2>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300">
                <div className="w-2 h-2 rounded-full bg-primary-500 mt-2"></div>
                <div>
                  <p className="text-sm text-gray-300">New visitor from Nigeria viewed your portfolio</p>
                  <p className="text-xs text-gray-500 mt-1">2 minutes ago</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass rounded-2xl p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaCode className="text-primary-400" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <button className="p-4 rounded-xl bg-primary-500/20 hover:bg-primary-500/30 transition-all duration-300 text-primary-400 text-sm font-medium">
              Add Project
            </button>
            <button className="p-4 rounded-xl bg-accent-500/20 hover:bg-accent-500/30 transition-all duration-300 text-accent-400 text-sm font-medium">
              Write Blog
            </button>
            <button className="p-4 rounded-xl bg-green-500/20 hover:bg-green-500/30 transition-all duration-300 text-green-400 text-sm font-medium">
              Add Experience
            </button>
            <button className="p-4 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 transition-all duration-300 text-purple-400 text-sm font-medium">
              View Analytics
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;